/**
 * HOA Homeowner Portal — public auth pages + protected dashboard.
 *
 * All components in one file to stay compact. Routes:
 *   /hoa/login              -> HoaLoginPage
 *   /hoa/forgot-password    -> HoaForgotPasswordPage
 *   /hoa/reset-password     -> HoaResetPasswordPage (reads ?token=)
 *   /hoa/set-password       -> HoaSetPasswordPage   (reads ?token=)
 *   /hoa/magic              -> HoaMagicLinkPage     (reads ?token=)
 *   /hoa/dashboard          -> HoaDashboardPage     (protected)
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, LogIn, Mail, Home, LogOut, Receipt, CreditCard, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import hoaApi, { formatApiErrorDetail } from '@/utils/hoaApi';
import { useHoaAuth } from '@/contexts/HoaAuthContext';

const GOLD = '#BCA480';
const DARK = '#434345';

// -----------------------------------------------------------------------------
// Shared chrome
// -----------------------------------------------------------------------------

const AuthShell = ({ title, subtitle, children, footer }) => (
  <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: '#f4efe6' }}>
    <div className="w-full max-w-md">
      <Link to="/" className="flex items-center justify-center mb-8 group">
        <div className="text-center">
          <div className="text-xs tracking-[0.3em] font-bold" style={{ color: GOLD }}>BESPOKE REALTY</div>
          <div className="text-[11px] uppercase tracking-widest text-gray-500 mt-0.5">Homeowner Portal</div>
        </div>
      </Link>
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold mb-1" style={{ color: DARK }}>{title}</h1>
        {subtitle && <p className="text-sm text-gray-600 mb-6">{subtitle}</p>}
        {children}
      </div>
      {footer && <div className="text-center mt-5 text-sm text-gray-600">{footer}</div>}
    </div>
  </div>
);

// -----------------------------------------------------------------------------
// LOGIN
// -----------------------------------------------------------------------------

export const HoaLoginPage = () => {
  const { homeowner, signIn } = useHoaAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  if (homeowner) return <Navigate to="/hoa/dashboard" replace />;

  const loginPassword = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await hoaApi.post('/hoa/auth/login', { email, password });
      signIn(data.access_token, data.homeowner);
      toast.success('Welcome back.');
      navigate('/hoa/dashboard');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Login failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const requestMagicLink = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await hoaApi.post('/hoa/auth/magic-link/request', { email });
      setMagicSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Sign in to your portal"
      subtitle="View your dues balance, ledger, and association announcements."
      footer={<Link to="/" className="hover:underline" style={{ color: GOLD }}>← Back to bespokerealty.net</Link>}
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full mb-6">
          <TabsTrigger value="password" data-testid="hoa-login-tab-password">Password</TabsTrigger>
          <TabsTrigger value="magic" data-testid="hoa-login-tab-magic">Email me a link</TabsTrigger>
        </TabsList>

        <TabsContent value="password">
          <form onSubmit={loginPassword} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="hoa-login-email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="hoa-login-password" />
              <div className="text-right mt-1">
                <Link to="/hoa/forgot-password" className="text-xs hover:underline" style={{ color: GOLD }}>Forgot password?</Link>
              </div>
            </div>
            <Button type="submit" className="w-full text-white" style={{ backgroundColor: GOLD }} disabled={submitting} data-testid="hoa-login-submit">
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in…</> : <><LogIn className="h-4 w-4 mr-2" /> Sign In</>}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="magic">
          {!magicSent ? (
            <form onSubmit={requestMagicLink} className="space-y-4">
              <p className="text-sm text-gray-600">
                We'll email you a one-time sign-in link. No password needed.
              </p>
              <div>
                <Label htmlFor="magic-email">Email</Label>
                <Input id="magic-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="hoa-magic-email" />
              </div>
              <Button type="submit" className="w-full text-white" style={{ backgroundColor: GOLD }} disabled={submitting} data-testid="hoa-magic-submit">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : <><Mail className="h-4 w-4 mr-2" /> Email Me a Link</>}
              </Button>
            </form>
          ) : (
            <div className="text-center py-4" data-testid="hoa-magic-sent">
              <Mail className="h-10 w-10 mx-auto mb-3" style={{ color: GOLD }} />
              <h3 className="font-bold text-lg mb-1" style={{ color: DARK }}>Check your inbox.</h3>
              <p className="text-sm text-gray-600">
                If an account with that email exists, we've sent a sign-in link. It expires in 20 minutes.
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AuthShell>
  );
};

// -----------------------------------------------------------------------------
// Forgot / Reset / Set password (shared pattern)
// -----------------------------------------------------------------------------

export const HoaForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await hoaApi.post('/hoa/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title="Reset your password" subtitle="We'll email you a reset link.">
      {!sent ? (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="forgot-email">Email</Label>
            <Input id="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="hoa-forgot-email" />
          </div>
          <Button type="submit" className="w-full text-white" style={{ backgroundColor: GOLD }} disabled={submitting} data-testid="hoa-forgot-submit">
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : 'Send Reset Link'}
          </Button>
          <div className="text-center text-sm">
            <Link to="/hoa/login" className="hover:underline" style={{ color: GOLD }}>← Back to sign in</Link>
          </div>
        </form>
      ) : (
        <div className="text-center py-4" data-testid="hoa-forgot-sent">
          <Mail className="h-10 w-10 mx-auto mb-3" style={{ color: GOLD }} />
          <h3 className="font-bold mb-1" style={{ color: DARK }}>Check your inbox.</h3>
          <p className="text-sm text-gray-600">If an account exists, we've sent a reset link. It expires in 1 hour.</p>
        </div>
      )}
    </AuthShell>
  );
};

const TokenPasswordForm = ({ title, subtitle, endpoint, successMsg }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useHoaAuth();
  const params = new URLSearchParams(location.search);
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { toast.error('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const { data } = await hoaApi.post(endpoint, { token, password });
      signIn(data.access_token, data.homeowner);
      toast.success(successMsg);
      navigate('/hoa/dashboard');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid link" subtitle="This link is missing its token. Please use the latest email we sent.">
        <Link to="/hoa/login" className="block text-center hover:underline" style={{ color: GOLD }}>← Back to sign in</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={title} subtitle={subtitle}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="new-pw">New password</Label>
          <Input id="new-pw" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="hoa-pw-new" />
        </div>
        <div>
          <Label htmlFor="confirm-pw">Confirm password</Label>
          <Input id="confirm-pw" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="hoa-pw-confirm" />
        </div>
        <Button type="submit" className="w-full text-white" style={{ backgroundColor: GOLD }} disabled={submitting} data-testid="hoa-pw-submit">
          {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : 'Save Password & Sign In'}
        </Button>
      </form>
    </AuthShell>
  );
};

export const HoaSetPasswordPage = () => (
  <TokenPasswordForm
    title="Set up your password"
    subtitle="Welcome. Set a password for your Bespoke Homeowner Portal."
    endpoint="/hoa/auth/set-password"
    successMsg="Welcome — you're all set."
  />
);

export const HoaResetPasswordPage = () => (
  <TokenPasswordForm
    title="Choose a new password"
    subtitle="Enter and confirm your new password."
    endpoint="/hoa/auth/reset-password"
    successMsg="Password updated."
  />
);

// -----------------------------------------------------------------------------
// MAGIC LINK verify (one-shot)
// -----------------------------------------------------------------------------

export const HoaMagicLinkPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signIn } = useHoaAuth();
  const [status, setStatus] = useState('verifying');  // verifying | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = new URLSearchParams(location.search).get('token');
    if (!token) {
      setStatus('error');
      setMessage('This link is missing its token.');
      return;
    }
    hoaApi.post('/hoa/auth/magic-link/verify', { token })
      .then(({ data }) => {
        signIn(data.access_token, data.homeowner);
        navigate('/hoa/dashboard', { replace: true });
      })
      .catch((err) => {
        setStatus('error');
        setMessage(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to sign you in.');
      });
    // eslint-disable-next-line
  }, []);

  if (status === 'verifying') {
    return (
      <AuthShell title="Signing you in…" subtitle="Give us a second.">
        <div className="flex justify-center py-6" data-testid="hoa-magic-verifying">
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: GOLD }} />
        </div>
      </AuthShell>
    );
  }
  return (
    <AuthShell title="This link didn't work" subtitle={message} data-testid="hoa-magic-error">
      <Link to="/hoa/login" className="block text-center text-white px-4 py-2 rounded font-semibold" style={{ backgroundColor: GOLD }}>
        Try signing in again
      </Link>
    </AuthShell>
  );
};

// -----------------------------------------------------------------------------
// DASHBOARD (protected)
// -----------------------------------------------------------------------------

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const HoaDashboardPage = () => {
  const { homeowner, signOut } = useHoaAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [paymentBanner, setPaymentBanner] = useState(null);

  const loadDashboard = () => {
    return hoaApi.get('/hoa/me/dashboard')
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load dashboard. Please try again.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (homeowner === false) {
      navigate('/hoa/login', { replace: true });
      return;
    }
    if (!homeowner) return;
    loadDashboard();
    // eslint-disable-next-line
  }, [homeowner, navigate]);

  // Handle Stripe return params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payment = params.get('payment');
    const sessionId = params.get('session_id');
    if (payment === 'success' && sessionId) {
      setPaymentBanner({ type: 'success', message: 'Payment received. Your ledger will update within a few seconds.' });
      // Poll for the ledger to show the payment
      const poll = async () => {
        for (let i = 0; i < 8; i++) {
          try {
            const { data: s } = await hoaApi.get(`/hoa/me/pay/status/${sessionId}`);
            if (s.status === 'paid') {
              await loadDashboard();
              setPaymentBanner({ type: 'success', message: `✓ $${Number(s.amount).toFixed(2)} payment confirmed.` });
              return;
            }
          } catch (_) { /* keep polling */ }
          await new Promise((r) => setTimeout(r, 1500));
        }
        setPaymentBanner({ type: 'pending', message: 'Payment is processing. ACH transfers can take 3–5 business days to settle.' });
        await loadDashboard();
      };
      poll();
      // Clear the query string so a reload doesn't retrigger
      window.history.replaceState({}, '', '/hoa/dashboard');
    } else if (payment === 'cancelled') {
      setPaymentBanner({ type: 'info', message: 'Payment cancelled. Nothing was charged.' });
      window.history.replaceState({}, '', '/hoa/dashboard');
    }
    // eslint-disable-next-line
  }, []);

  const payNow = async (amount) => {
    const amt = Number(amount);
    if (!amt || amt < 1) { toast.error('Enter an amount of $1.00 or more.'); return; }
    setPaying(true);
    try {
      const { data: s } = await hoaApi.post('/hoa/me/pay/create-session', {
        amount: amt,
        origin_url: window.location.origin,
      });
      window.location.href = s.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start payment.');
      setPaying(false);
    }
  };

  if (homeowner === null || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f4efe6' }}>
        <Loader2 className="h-10 w-10 animate-spin" style={{ color: GOLD }} />
      </div>
    );
  }

  if (!data) return null;

  const balancePositive = data.current_balance > 0;
  const suggestedAmount = balancePositive ? data.current_balance : (data.unit.monthly_dues || 0);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9f7f3' }} data-testid="hoa-dashboard-page">
      {/* Header */}
      <header className="border-b border-[#e6dec7] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs tracking-[0.3em] font-bold" style={{ color: GOLD }}>BESPOKE REALTY</div>
            <div className="text-[11px] uppercase tracking-widest text-gray-500 mt-0.5">Homeowner Portal</div>
          </div>
          <button
            onClick={() => { signOut(); navigate('/hoa/login'); }}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#BCA480]"
            data-testid="hoa-signout-btn"
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {paymentBanner && (
          <div
            className={`mb-6 p-4 rounded-lg border text-sm ${
              paymentBanner.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' :
              paymentBanner.type === 'pending' ? 'bg-amber-50 border-amber-200 text-amber-800' :
              'bg-blue-50 border-blue-200 text-blue-800'
            }`}
            data-testid="hoa-payment-banner"
          >
            {paymentBanner.message}
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ color: DARK }} data-testid="hoa-welcome">
            Welcome, {data.homeowner.name.split(' ')[0] || 'homeowner'}.
          </h1>
          <p className="text-gray-600 mt-1">
            {data.unit.address} · <strong>{data.association.name}</strong>
          </p>
        </div>

        {/* Balance + unit cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          <div className="md:col-span-2 bg-white rounded-lg p-8 border border-[#e6dec7] shadow-sm">
            <div className="text-[11px] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: GOLD }}>
              Current Balance
            </div>
            <div className="text-5xl font-bold" style={{ color: balancePositive ? '#b85c3b' : '#2e7d4c' }} data-testid="hoa-balance">
              {money(data.current_balance)}
            </div>
            <div className="text-sm text-gray-600 mt-2">
              {balancePositive ? 'Amount due.' : "You're all paid up. Nice."}
            </div>
            {balancePositive && (
              <div className="mt-5 pt-5 border-t border-gray-100 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <Button
                  onClick={() => payNow(data.current_balance)}
                  disabled={paying}
                  className="text-white sm:flex-1"
                  style={{ backgroundColor: GOLD }}
                  data-testid="hoa-pay-full-btn"
                >
                  {paying ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting payment…</> : <><CreditCard className="h-4 w-4 mr-2" /> Pay {money(data.current_balance)} Now</>}
                </Button>
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-gray-400">or</span>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Custom amount"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="w-32"
                    data-testid="hoa-pay-custom-amount"
                  />
                  <Button
                    variant="outline"
                    onClick={() => payNow(customAmount)}
                    disabled={paying || !customAmount}
                    data-testid="hoa-pay-custom-btn"
                  >
                    Pay
                  </Button>
                </div>
              </div>
            )}
            {!balancePositive && (
              <div className="mt-5 pt-5 border-t border-gray-100">
                <Button
                  onClick={() => payNow(data.unit.monthly_dues || 0)}
                  disabled={paying || !data.unit.monthly_dues}
                  variant="outline"
                  className="w-full"
                  data-testid="hoa-pay-prepay-btn"
                >
                  <CreditCard className="h-4 w-4 mr-2" /> Prepay next month ({money(data.unit.monthly_dues)})
                </Button>
              </div>
            )}
            <div className="text-[11px] text-gray-500 mt-3 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Secure payment via Stripe · ACH + Credit/Debit · Receipt emailed instantly
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-[#e6dec7] shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Home className="h-4 w-4" style={{ color: GOLD }} />
              <div className="text-[11px] tracking-[0.2em] uppercase font-bold" style={{ color: GOLD }}>Your Unit</div>
            </div>
            <div className="text-sm space-y-1 text-gray-700">
              <div><strong>Address:</strong> {data.unit.address}</div>
              {data.unit.unit_number && <div><strong>Unit #:</strong> {data.unit.unit_number}</div>}
              <div><strong>Monthly Dues:</strong> {money(data.unit.monthly_dues)}</div>
              <div className="pt-2 border-t border-gray-100 mt-2"><strong>Association:</strong> {data.association.name}</div>
            </div>
          </div>
        </div>

        {/* Ledger */}
        <div className="bg-white rounded-lg border border-[#e6dec7] shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-6 py-4 border-b border-[#e6dec7]">
            <Receipt className="h-4 w-4" style={{ color: GOLD }} />
            <h2 className="font-bold" style={{ color: DARK }}>Ledger</h2>
            <span className="text-xs text-gray-500 ml-2">{data.ledger.length} entries</span>
          </div>
          {data.ledger.length === 0 ? (
            <div className="text-center text-gray-500 py-10" data-testid="hoa-ledger-empty">No activity yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="hoa-ledger-table">
                <thead className="bg-[#faf8f4] text-left">
                  <tr>
                    <th className="px-6 py-3 font-semibold text-gray-600 text-xs tracking-wider uppercase">Date</th>
                    <th className="px-6 py-3 font-semibold text-gray-600 text-xs tracking-wider uppercase">Memo</th>
                    <th className="px-6 py-3 font-semibold text-gray-600 text-xs tracking-wider uppercase">Type</th>
                    <th className="px-6 py-3 font-semibold text-gray-600 text-xs tracking-wider uppercase text-right">Amount</th>
                    <th className="px-6 py-3 font-semibold text-gray-600 text-xs tracking-wider uppercase text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ledger.map((e) => (
                    <tr key={e.id} className="border-t border-gray-100 hover:bg-[#faf8f4]/50">
                      <td className="px-6 py-3 text-gray-600">{new Date(e.date).toLocaleDateString()}</td>
                      <td className="px-6 py-3">{e.memo}</td>
                      <td className="px-6 py-3">
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                          e.type === 'charge' ? 'bg-orange-100 text-orange-700' :
                          e.type === 'payment' ? 'bg-green-100 text-green-700' :
                          e.type === 'credit' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{e.type}</span>
                      </td>
                      <td className="px-6 py-3 text-right font-mono">
                        {e.type === 'charge' || (e.type === 'adjustment' && e.amount > 0) ? '+' : '−'}{money(Math.abs(e.amount))}
                      </td>
                      <td className="px-6 py-3 text-right font-mono text-gray-600">{money(e.running_balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-10 p-6 rounded-lg border border-[#e6dec7]" style={{ backgroundColor: '#faf8f4' }}>
          <h3 className="font-bold mb-2" style={{ color: DARK }}>Prefer to pay by check?</h3>
          <p className="text-sm text-gray-600 leading-relaxed">
            Mail checks payable to Bespoke Realty · 7927 Players Forest Drive, Memphis, TN 38119. Include your unit address on the memo line. Payments post within 2 business days of receipt.
          </p>
        </div>
      </main>
    </div>
  );
};
